import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {HttpService} from '../../core';
import {UserService} from '../../core/services/user.service';
import {fromEvent, Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, finalize} from 'rxjs/operators';
import {MediaChange, MediaObserver} from '@angular/flex-layout';
import {MatGridList} from '@angular/material/grid-list';
import {PageEvent} from '@angular/material/paginator';
import {ProjectDetailTabService} from 'src/app/core/services/projectDetailTab.service';
import {AddProjectDialogComponent} from './add-project-dialog/add-project-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {WelcomeDialogComponent} from '../../shared/components/dialog/welcome-dialog/welcome-dialog.component';

/**
 * @class ProjectComponent
 * @memberOf ProjectRoutingModule
 * @description This is a component which control the project of the system
 */
@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss'],
})
export class ProjectComponent
  implements OnInit, AfterViewInit, AfterContentInit {
  companyId: any;
  projects$: any;
  isFirstTimeLogin = false;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  currentSearch: string;

  isLoading: boolean;
  layout = 'list';
  user: any;
  highestRole: any;

  selected = 'any';
  selectList: any[] = [
    'any',
    'Favourites',
    'Project name',
    'Project status',
    'Start date',
    'End date',
    'Sunset date',
  ];

  @ViewChild('projectSearchInput', {static: true})
  projectSearchInput: ElementRef;
  @ViewChild('grid') grid: MatGridList;

  gridByBreakpoint = {
    xl: 4,
    lg: 4,
    md: 3,
    sm: 2,
    xs: 1,
  };
  gridCol: number;

  constructor(
    private httpService: HttpService,
    private userService: UserService,
    private tabService: ProjectDetailTabService,
    private mediaObserver: MediaObserver,
    private dialog: MatDialog
  ) {
    this.isLoading = false;
  }

  ngOnInit() {
    this.initializeProperties();
    this.loadUser();
    this.tabService.reset();
  }

  initializeProperties() {
    this.page = 0;
    this.pageSize = 5;
    this.isLoading = true;
    this.total = 0;
    this.totalPages = 0;
    this.first = true;
    this.last = false;
    this.currentSearch = '';
  }

  loadUser() {
    this.userService.currentUser.subscribe((user: any) => {
      this.user = user;
      console.log('user info:', this.user);
      this.highestRole = user.roles[0];
      this.isFirstTimeLogin = !this.user?.firstName;
      if (this.isFirstTimeLogin) {
        const dialogRef = this.dialog.open(WelcomeDialogComponent, {
          width: '43%',
          maxWidth: '800px',
          hasBackdrop: true,
          data: {record: this.user.company.id}
        });
        dialogRef.backdropClick().subscribe(() => {
          this.dialog.closeAll();
        });
        dialogRef.afterClosed()
          .subscribe((data: any) => {
            if (data) {
              window.location.reload();
            }
          });
      }
      if (this.user?.company?.id) {
        this.loadProjects();
      } else {
        this.projects$ = null;
      }
    });
  }

  loadProjects() {
    // Indicate that the project list is loading until the response arrives
    this.isLoading = true;
    // Fetch paginated project list using backend pagination
    this.httpService
      .searchProjects(
        this.user.company.id,
        this.currentSearch || '',
        this.page,
        this.pageSize
      )
      .pipe(
        finalize(() => (this.isLoading = false))
      )
      .subscribe((res: any) => {
        console.log('searchProjects=>', res);
        const pageResp = res?.data?.[0];
        this.projects$ = pageResp?.content || [];
        // Capture pagination metadata from response
        this.total = pageResp?.totalElements || 0;
        this.totalPages = pageResp?.totalPages || 0;
        this.first = !!pageResp?.first;
        this.last = !!pageResp?.last;
        // Load draft projects after main list arrives (does not block loading spinner)
        this.loadDraftProjects();
      });
  }

  loadDraftProjects() {
    this.httpService.getDraftProjectList().subscribe((r: any) => {
      if (r.code === 1) {
        const data = r.data;
        data.forEach((i: any) => i.draft = true);
        this.projects$ = this.projects$.concat(data);
        this.sortProjects();
        console.log('getDraftProjectList=>', this.projects$);
      }
    });
  }

  sortProjects() {
    this.projects$.sort((a: any, b: any) => {
      return (b.draft ? 1 : 0) - (a.draft ? 1 : 0);
    });
  }


  ngAfterContentInit() {
    this.mediaObserver.asObservable().subscribe((change: MediaChange[]) => {
      this.gridCol = this.gridByBreakpoint[change[0].mqAlias];
    });
  }

  ngAfterViewInit(): void {
    fromEvent(this.projectSearchInput.nativeElement, 'keyup')
      .pipe(
        // get value
        map((event: any) => {
          return event.target.value;
        }),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((text: string) => {
        this.projects$ = null;
        // Reset page index when search keyword changes
        this.page = 0;
        this.currentSearch = text || '';
        if (this.highestRole.weight >= 1000) {
          // Dev admin search with pagination
          this.isLoading = true; // Show loader while searching
          this.httpService
            .searchProjects(
              this.user.company.id,
              this.currentSearch,
              this.page,
              this.pageSize
            )
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe((res: any) => {
              const pageResp = res?.data?.[0];
              this.projects$ = pageResp?.content || [];
              this.total = pageResp?.totalElements || 0;
              this.totalPages = pageResp?.totalPages || 0;
              this.first = !!pageResp?.first;
              this.last = !!pageResp?.last;
            });

        } else {
          this.isLoading = true;
          this.httpService
            .searchProjectsByUser(text)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe((res: any) => {
              this.projects$ = res.data[0].content;
            });
        }
      });
  }

  addProject() {
    const dialogRef = this.dialog.open(AddProjectDialogComponent, {
      width: '816px',
      height: '800px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      console.log('addProject closed: %o', result);
      setTimeout(() => {
        console.log('reload projects');
        this.ngOnInit();
      }, 1000);
    });
  }

  /**
   * @function onPageChange
   * @description Handle paginator page change event and reload data
   */
  onPageChange(event: PageEvent) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProjects();
  }
}
